import { useEffect, useState } from "react";
import { Page } from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import RecordingDrawer from "../components/RecordingDrawer";
import { api, download, notifyErr, fmtDate, fmtSec } from "../lib/api";
import { useCustomers } from "../lib/hooks";

const LABELS = { forte: "Lead forte", potenziale: "Lead potenziale", nessuna: "Nessun recupero", incerta: "Da verificare" };

export default function CommercialIntelligence() {
  const { customers } = useCustomers();
  const [cid, setCid] = useState("");
  const [allCases, setAllCases] = useState(false);
  const [exportMode, setExportMode] = useState("new");
  const [exportStatus, setExportStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [dateOrder, setDateOrder] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const visibleRows = rows.filter(r => {
    const date = r.call_date ? r.call_date.slice(0, 10) : "";
    if (dateFrom && (!date || date < dateFrom)) return false;
    if (dateTo && (!date || date > dateTo)) return false;
    return true;
  }).sort((a, b) => {
    const left = a.call_date || "";
    const right = b.call_date || "";
    return dateOrder === "desc"
      ? right.localeCompare(left)
      : left.localeCompare(right);
  });
  useEffect(() => {
    if (!cid && customers?.length) setCid(customers[0].id);
  }, [cid, customers]);
  useEffect(() => {
    if (!cid) return;
    setLoading(true);
    Promise.all([
      api.get(`/ci/v6/leads`, { params: { customer_id: cid, all_cases: allCases, limit: 2000 } }),
      api.get(`/ci/v6/stats`, { params: { customer_id: cid } }),
      api.get(`/ci/v6/export-status`, { params: { customer_id: cid } }),
    ]).then(([res, s, e]) => { setRows(res.data.leads || []); setStats(s.data); setExportStatus(e.data); })
      .catch(notifyErr).finally(() => setLoading(false));
  }, [cid, allCases]);
  const archiveLeads = async (ids) => {
    if (!cid || archiving || !ids.length) return;
    if (!window.confirm(`Archiviare ${ids.length} lead già esportati? Saranno nascosti dall'elenco, ma registrazioni e analisi resteranno disponibili.`)) return;
    setArchiving(true);
    try {
      await api.post("/ci/v6/archive-exported", {
        customer_id: cid,
        recording_ids: ids
      });
      setSelected([]);
      await refresh();
    } catch (e) {
      notifyErr(e);
    } finally {
      setArchiving(false);
    }
  };
  const exportedVisible = visibleRows.filter(r => Boolean(r.exported_at));
  const selectedVisible = exportedVisible.filter(r => selected.includes(r.recording_id));
  const giveFeedback = async (e, r, accurate) => {
    e.stopPropagation();
    try {
      await api.put(`/ci/v6/feedback/${r.recording_id}`, { customer_id: cid, accurate });
      setRows(previous => previous.map(item => item.recording_id === r.recording_id ?
        { ...item, trust_level: accurate ? "human_verified" : "human_rejected" } : item));
    } catch (error) { notifyErr(error); }
  };
  const refresh = async () => {
    if (!cid) return;
    const [r, s, e] = await Promise.all([
      api.get('/ci/v6/leads', { params: { customer_id: cid, all_cases: allCases, limit: 2000 } }),
      api.get('/ci/v6/stats', { params: { customer_id: cid } }),
      api.get('/ci/v6/export-status', { params: { customer_id: cid } }),
    ]);
    setRows(r.data.leads || []);
    setStats(s.data);
    setExportStatus(e.data);
  };
  // Aggiorna la pagina ogni 30 s: nuove chiamate qualificate e registro export.
  // Il timer viene chiuso quando si cambia pagina o azienda.
  useEffect(() => {
    if (!cid) return;
    let busy = false;
    const interval = setInterval(async () => {
      if (busy) return;
      busy = true;
      try {
        await refresh();
      } catch (_) {
        // Evita avvisi ripetuti durante brevi interruzioni di rete.
      } finally {
        busy = false;
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [cid, allCases]);
  const exportLeads = async () => {
    if (!cid || exporting) return;
    setExporting(true);
    try {
      const p = new URLSearchParams({ customer_id: cid, all_cases: String(allCases), mode: exportMode });
      await download(`/ci/v6/leads.csv?${p.toString()}`,
        `commercial_intelligence_${exportMode === "new" ? "solo_nuovi" : "tutti"}.csv`);
      await refresh();
    } catch (e) { notifyErr(e); }
    finally { setExporting(false); }
  };
  const redownloadLast = async () => {
    if (!cid || exporting) return;
    setExporting(true);
    try {
      await download(`/ci/v6/last-export.csv?${new URLSearchParams({ customer_id: cid })}`,
        'commercial_intelligence_ultimo_export.csv');
    } catch (e) { notifyErr(e); }
    finally { setExporting(false); }
  };
  return (
    <Page title="Commercial Intelligence" subtitle="Ricostruzione delle esigenze e opportunità di recupero dalle telefonate complete. Valutazioni AI da verificare: la segnalazione non autorizza il ricontatto."
      actions={<div className="flex flex-wrap gap-2 items-center">
        <CustomerSelect customers={customers || []} value={cid} onChange={setCid} />
        <select aria-label="Modalità esportazione" className="border rounded px-2 py-1.5 text-sm" value={exportMode} onChange={e => setExportMode(e.target.value)}>
          <option value="new">Esporta solo nuovi lead</option>
          <option value="all">Esporta tutti i lead</option>
        </select>
        <button className="border px-3 py-1.5 rounded text-sm" disabled={!cid || exporting} onClick={exportLeads}>
          {exporting ? "Esportazione…" : "Esporta CSV"}
        </button>
        <button className="border px-3 py-1.5 rounded text-sm" disabled={!cid || exporting || !exportStatus?.can_redownload} onClick={redownloadLast} title="Riscarica l'ultima selezione senza registrare una nuova esportazione">Riscarica ultimo CSV</button>
      </div>}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {[["Telefonate V6", stats?.analizzate_v6 ?? 0], ["Lead forti", stats?.per_esito?.forte ?? 0], ["Lead potenziali", stats?.per_esito?.potenziale ?? 0], ["Da verificare", stats?.per_esito?.incerta ?? 0], ["Opposizione contatto", stats?.non_contattare ?? 0]].map(([k,v]) =>
          <div key={k} className="p-4 bg-white border rounded-sm"><div className="text-xs text-neutral-500">{k}</div><div className="font-semibold text-2xl mt-1">{v}</div></div>
        )}
      </div>
      <div className="flex gap-3 items-center mb-3 text-sm">
        <label className="flex gap-2 items-center"><input type="checkbox" checked={allCases} onChange={e => setAllCases(e.target.checked)} /> Mostra anche rifiuti e casi incerti</label>
        <span className="text-neutral-500">{loading ? "Aggiornamento…" : `${rows.length} risultati`}</span>
        <span className="text-neutral-500">Nuovi visibili: <strong>{rows.filter(r => !r.exported_at).length}</strong></span>
        <span className="text-neutral-500">Aggiornamento automatico ogni 30 s</span>
        <span className="text-neutral-500">Già esportati in azienda: <strong>{exportStatus?.exported_count ?? 0}</strong></span>
        {exportStatus?.last_export_at && <span className="text-neutral-500">Ultimo export: {fmtDate(exportStatus.last_export_at)} ({exportStatus.last_export_count} righe)</span>}
        <button className="border rounded px-2 py-1" onClick={() => { if (!cid) return; setLoading(true); refresh().catch(notifyErr).finally(() => setLoading(false)); }}>Aggiorna</button>
      </div>
      <div className="flex flex-wrap gap-3 items-end mb-3 p-3 bg-white border rounded-sm text-sm">
        <label className="flex flex-col gap-1">
          <span>Ordina per data</span>
          <select className="border rounded px-2 py-1.5" value={dateOrder} onChange={e => setDateOrder(e.target.value)}>
            <option value="desc">Più recenti prima</option>
            <option value="asc">Più vecchi prima</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span>Dal giorno</span>
          <input type="date" className="border rounded px-2 py-1.5" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span>Al giorno</span>
          <input type="date" className="border rounded px-2 py-1.5" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </label>
        <button className="border rounded px-3 py-1.5" onClick={() => { setDateFrom(""); setDateTo(""); setDateOrder("desc"); }}>
          Azzera filtri
        </button>
        <span className="text-neutral-500">{visibleRows.length} lead visualizzati</span>
      </div>
      <div className="flex flex-wrap gap-2 items-center mb-3 text-sm">
        <span>Selezionati: {selectedVisible.length}</span>
        <button className="border rounded px-3 py-1.5"
          disabled={archiving || !exportedVisible.length}
          onClick={() => setSelected(exportedVisible.map(r => r.recording_id))}>
          Seleziona esportati visibili
        </button>
        <button className="border rounded px-3 py-1.5"
          onClick={() => setSelected([])}>
          Deseleziona
        </button>
        <button className="border rounded px-3 py-1.5 text-red-700"
          disabled={archiving || !selectedVisible.length}
          onClick={() => archiveLeads(selectedVisible.map(r => r.recording_id))}>
          Archivia selezionati
        </button>
        <button className="border rounded px-3 py-1.5 text-red-700"
          disabled={archiving || !exportedVisible.length}
          onClick={() => archiveLeads(exportedVisible.map(r => r.recording_id))}>
          Archivia tutti gli esportati visibili
        </button>
      </div>
      <div className="bg-white border rounded-sm overflow-x-auto">
        <table className="w-full text-xs text-left"><thead className="bg-neutral-50 border-b"><tr>{["Seleziona", "Telefono", "Data", "Agente", "Durata", "Esito", "Cosa desidera", "Perché recuperabile", "Controelementi", "Esportazione", "Verifica"].map(h=><th key={h} className="p-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{visibleRows.map(r => <tr key={r.recording_id} className="border-b hover:bg-neutral-50 cursor-pointer" onClick={()=>setOpen(r.recording_id)}>
          <td className="p-3" onClick={e => e.stopPropagation()}>
            <input type="checkbox"
              aria-label="Seleziona lead esportato"
              disabled={!r.exported_at}
              checked={selected.includes(r.recording_id)}
              onChange={e => setSelected(prev => e.target.checked
                ? [...new Set([...prev, r.recording_id])]
                : prev.filter(id => id !== r.recording_id))}
            />
          </td>
          <td className="p-3 font-mono">{r.phone_number || "—"}</td>
          <td className="p-3 whitespace-nowrap">{fmtDate(r.call_date)}</td>
          <td className="p-3">{r.agent || "—"}</td>
          <td className="p-3">{fmtSec(r.duration_sec)}</td>
          <td className="p-3"><span className="font-medium">{LABELS[r.outcome] || r.outcome}</span>{r.do_not_contact && <div className="text-red-600">NON RICONTATTARE</div>}</td>
          <td className="p-3 min-w-44">{r.details?.desiderio_cliente || "—"}</td>
          <td className="p-3 min-w-52">{r.details?.perche_lead || "—"}</td>
          <td className="p-3 min-w-40">{r.details?.controelementi || "—"}</td>
          <td className="p-3 min-w-32">{r.exported_at ? <><span className="font-medium">Già esportato</span><div className="text-neutral-500">{fmtDate(r.exported_at)}</div></> : <span className="font-medium text-green-700">Nuovo</span>}</td>
          <td className="p-3" onClick={e=>e.stopPropagation()}>
            <div className="text-neutral-500 mb-1">{r.trust_level === "human_verified" ? "Confermata" : r.trust_level === "human_rejected" ? "Valutazione errata" : "Da verificare"}</div>
            <div className="flex gap-1">
              <button className="border px-2 py-1 rounded text-green-700" onClick={e=>giveFeedback(e,r,true)}>Corretta</button>
              <button className="border px-2 py-1 rounded text-red-700" onClick={e=>giveFeedback(e,r,false)}>Errata</button>
            </div>
          </td>
        </tr>)}
        {!visibleRows.length && <tr><td colSpan={11} className="p-8 text-center text-neutral-500">Nessuna telefonata qualificata V6 disponibile. Lo storico viene elaborato automaticamente a carico ridotto.</td></tr>}
        </tbody></table>
      </div>
      <RecordingDrawer recordingId={open} onClose={()=>setOpen(null)} onChanged={()=>{}} />
    </Page>
  );
}
